import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { Typography, Divider } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useFormContext } from 'react-hook-form';

import {
  CPDEnrollmentStatusEnum,
  CPDLicenseTypeEnum,
  ICPDLicense,
  CPDRegulatorTypeEnum,
  LicenseRenewalTypeEnum,
  ICPDCourse,
  ApplicantTypeEnum,
} from '../../../../interfaces';

import LicenseForm from '@components/molecules/cpdEnrollment/licenseInformation/licenseForm';
import { CPDEnrollmentFieldNames } from 'src/helpers/constants';
import {
  dateFormatThai,
  getBundleLicenseFieldNames,
  getCpdLicenseTitleKey,
  getLicenseFieldNames,
  getLicenseTypeName,
  toCapitalized,
} from 'src/helpers/utils';

type Props = {
  customerLicenses: ICPDLicense[];
  cpdCourses: ICPDCourse[];
};

type FormLicense = {
  applicantTypeName: string;
  licenseTypeName: string;
  applicantType: ApplicantTypeEnum;
  licenseType: CPDLicenseTypeEnum;
};

const BundleOICLicenseRenewalForm = ({ customerLicenses, cpdCourses }: Props) => {
  const { getValues } = useFormContext();
  const { t } = useTranslation();

  const [courseLicenses, setCourseLicenses] = useState<FormLicense[]>([]);

  const licenseFormFlag = getValues(CPDEnrollmentFieldNames.status) !== CPDEnrollmentStatusEnum.RegulatorUnChecked;

  useEffect(() => {
    const data: FormLicense[] = [];

    for (const course of cpdCourses) {
      const courseLicense = {
        applicantTypeName: toCapitalized(course.applicantType),
        licenseTypeName: getLicenseTypeName(course.licenseTypes[0]),
        applicantType: course.applicantType,
        licenseType: course.licenseTypes[0],
      };

      const isAlreadyInArray = data.some(
        (item) => item.applicantType === courseLicense.applicantType && item.licenseType === courseLicense.licenseType,
      );

      if (!isAlreadyInArray) data.push(courseLicense);
    }

    const sortByLicenseTypeName = (a: FormLicense, b: FormLicense) => (a.licenseType >= b.licenseType ? -1 : 1);
    const sortByApplicantTypeName = (a: FormLicense, b: FormLicense) => (a.applicantType >= b.applicantType ? -1 : 1);

    const sortedData = data.sort(sortByLicenseTypeName).sort(sortByApplicantTypeName);

    setCourseLicenses(sortedData);
  }, []);

  return (
    <>
      {courseLicenses.map((courseLicense) => {
        const { licenseNoFieldName, licenseStartDateFieldName, licenseEndDateFieldName } = getBundleLicenseFieldNames(
          courseLicense.applicantTypeName,
          courseLicense.licenseTypeName,
        );

        const customerLicense = customerLicenses?.find(
          (license) =>
            license.licenseType === courseLicense.licenseType && license.applicantType === courseLicense.applicantType,
        );

        return (
          <>
            {cpdCourses.length > 1 && (
              <>
                <Typography sx={{ typography: { md: 'h3', xs: 'h6' }, mt: { xs: '30px', lg: '50px' } }}>
                  {t(
                    getCpdLicenseTitleKey(
                      CPDRegulatorTypeEnum.OIC,
                      LicenseRenewalTypeEnum.NotSet,
                      courseLicense.applicantType,
                      courseLicense.licenseType as CPDLicenseTypeEnum,
                    ),
                  )}
                </Typography>
                <Divider sx={{ my: { xs: '8px', md: '12px' } }} />
              </>
            )}

            <LicenseForm
              licenseFieldName={licenseNoFieldName}
              startDateFieldName={licenseStartDateFieldName}
              endDateFieldName={licenseEndDateFieldName}
              license={customerLicense?.licenseNo}
              startDate={customerLicense?.createdDate ? dateFormatThai(customerLicense?.createdDate!) : undefined}
              endDate={customerLicense?.expiredDate ? dateFormatThai(customerLicense?.expiredDate!) : undefined}
              disabled={licenseFormFlag}
            />
          </>
        );
      })}
    </>
  );
};

export default BundleOICLicenseRenewalForm;
